import type { Shift, UserListEntry, SessionUser } from "../api/types";

/**
 * Users eligible as assignees in shift modals.
 * Owner mode: active managers, florists, housekeeping, owners, and admins
 * (API allows owner to schedule anyone).
 * Manager mode: self, florists already visible on shifts at this shop (same
 * pattern as before), plus every active housekeeping person from the
 * directory — G-103 / OD-72: cleaners belong to no shop, so they can only be
 * found in the directory, never derived from this shop's shifts.
 */
export function buildScheduleCandidateUsers(params: {
  mode: "owner" | "manager";
  users: UserListEntry[];
  shifts: Shift[];
  currentUser: SessionUser;
}): UserListEntry[] {
  const { mode, users, shifts, currentUser } = params;
  if (mode === "owner") {
    return users.filter(
      (u) =>
        u.isActive &&
        (u.role === "MANAGER" ||
          u.role === "FLORIST" ||
          u.role === "HOUSEKEEPING" ||
          u.role === "OWNER" ||
          u.role === "ADMIN"),
    );
  }
  const fromShifts = new Map<string, UserListEntry>();
  for (const s of shifts) {
    if (s.user.id === currentUser.id) continue;
    fromShifts.set(s.user.id, {
      id: s.user.id,
      email: s.user.email,
      fullName: s.user.fullName,
      role: s.user.role,
      isActive: true,
      createdAt: s.createdAt,
      locationAssignments: [],
    });
  }
  const list = Array.from(fromShifts.values()).filter(
    (u) => u.role === "FLORIST",
  );
  for (const u of users) {
    if (u.isActive && u.role === "HOUSEKEEPING" && u.id !== currentUser.id) {
      list.push(u);
    }
  }
  list.unshift({
    id: currentUser.id,
    email: currentUser.email,
    fullName: currentUser.fullName,
    role: currentUser.role,
    isActive: true,
    createdAt: new Date().toISOString(),
    locationAssignments: [],
  });
  return list;
}
